import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Clock, Calendar, Tag, MapPin, Info, Navigation } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const ActionCard = ({ action, onPrimaryAction, primaryActionLabel, primaryActionDisabled, primaryActionLoading }) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!action.countdown) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const target = new Date(action.countdown.targetDate).getTime();
      const diff = target - now;

      if (diff <= 0) {
        setCountdown(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [action.countdown]);

  const handleActionClick = () => {
    if (typeof onPrimaryAction === "function") {
      onPrimaryAction(action);
      return;
    }
    if (action.isActive) {
      navigate(`/scan/${action.slug}`);
    }
  };

  const getDiscountDisplay = () => {
    if (action.discountType === "PERCENTAGE") {
      return `${action.discountValue}% Rabatt`;
    } else if (action.discountType === "FIXED") {
      return `${action.discountValue}€ Rabatt`;
    } else if (action.discountType === "FREE_ITEM") {
      return "Gratis Artikel";
    } else {
      return "Sonderangebot";
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="bg-background rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      {/* Banner Image */}
      {action.bannerUrl ? (
        <div className="h-48 relative overflow-hidden">
          {/* Blurred background */}
          <div className="absolute inset-0">
            <img
              src={action.bannerUrl}
              alt=""
              className="w-full h-full object-cover blur-xl brightness-50 scale-110"
            />
          </div>
          {/* Main image */}
          <img
            src={action.bannerUrl}
            alt={action.title}
            className="relative w-full h-full object-contain z-10"
          />
          {action.isUpcoming && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-yellow-500 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Demnächst
              </Badge>
            </div>
          )}
        </div>
      ) : (
        <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center relative">
          <Tag className="w-16 h-16 text-white opacity-50" />
          {action.isUpcoming && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-yellow-500 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Demnächst
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {/* Organization */}
        <div className="flex items-center gap-3 mb-4">
          {action.organization.logoUrl ? (
            <img
              src={action.organization.logoUrl}
              alt={action.organization.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-border"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {action.organization.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-semibold text-foreground">{action.organization.name}</p>
            <p className="text-sm text-muted-foreground">{action.campaign.name}</p>
          </div>
        </div>

        {/* Title & Discount */}
        <h3 className="text-xl font-bold text-foreground mb-2">{action.title}</h3>
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="secondary" className="text-lg font-bold">
            {getDiscountDisplay()}
          </Badge>
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Calendar className="w-4 h-4" />
          <span>
            {formatDate(action.requestStart)} - {formatDate(action.redeemEnd)}
          </span>
        </div>

        {/* Locations */}
        {action.locations && action.locations.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">
                  {(() => {
                    const uniqueCities = [...new Set(action.locations.map(l => l.city))];
                    const firstCity = uniqueCities[0];
                    const totalLocations = action.locations.length;
                    const additionalCities = uniqueCities.length - 1;
                    
                    let text = firstCity;
                    if (additionalCities > 0) {
                      text += ` +${additionalCities} ${additionalCities === 1 ? 'Stadt' : 'Städte'}`;
                    }
                    text += ` und ${totalLocations} Standort${totalLocations !== 1 ? 'e' : ''}`;
                    
                    return text;
                  })()}
                </span>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <button className="hover:bg-muted p-1.5 rounded-full transition-colors">
                    <Info className="w-4 h-4 text-blue-600" />
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Teilnehmende Standorte
                    </DialogTitle>
                  </DialogHeader>
                  
                  {/* List of all locations */}
                  <div className="space-y-3 mt-4">
                    {action.locations.map((location) => (
                      <div
                        key={location.id}
                        className="border rounded-lg p-4 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg mb-2">{location.name}</h4>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <p>{location.addressLine1}</p>
                              {location.addressLine2 && <p>{location.addressLine2}</p>}
                              <p className="font-medium">
                                {location.postalCode} {location.city}
                              </p>
                            </div>
                          </div>
                          {location.latitude && location.longitude && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                                const url = isIOS 
                                  ? `https://maps.apple.com/?daddr=${location.latitude},${location.longitude}`
                                  : `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`;
                                window.open(url, '_blank');
                              }}
                              className="flex-shrink-0"
                            >
                              <Navigation className="w-4 h-4 mr-2" />
                              Route
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Map with all locations */}
                  {(() => {
                    const locationsWithCoords = action.locations.filter(
                      l => l.latitude && l.longitude
                    );
                    
                    if (locationsWithCoords.length > 0) {
                      // Calculate center of all markers
                      const avgLat = locationsWithCoords.reduce(
                        (sum, l) => sum + parseFloat(l.latitude), 0
                      ) / locationsWithCoords.length;
                      const avgLon = locationsWithCoords.reduce(
                        (sum, l) => sum + parseFloat(l.longitude), 0
                      ) / locationsWithCoords.length;
                      
                      return (
                        <div className="mt-6">
                          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Kartenansicht
                          </h3>
                          <div className="h-80 rounded-lg overflow-hidden border">
                            <MapContainer
                              center={[avgLat, avgLon]}
                              zoom={locationsWithCoords.length === 1 ? 13 : 10}
                              style={{ height: "100%", width: "100%" }}
                            >
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              {locationsWithCoords.map((location) => (
                                <Marker
                                  key={location.id}
                                  position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
                                >
                                  <Popup>
                                    <div className="text-sm">
                                      <strong className="text-base">{location.name}</strong>
                                      <br />
                                      {location.addressLine1}
                                      {location.addressLine2 && (
                                        <>
                                          <br />
                                          {location.addressLine2}
                                        </>
                                      )}
                                      <br />
                                      {location.postalCode} {location.city}
                                    </div>
                                  </Popup>
                                </Marker>
                              ))}
                            </MapContainer>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </DialogContent>
              </Dialog>
            </div>
          </div>
        )}

        {/* Countdown or Status */}
        {action.isUpcoming && countdown ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-yellow-800 mb-2">
              Startet in:
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <div className="text-2xl font-bold text-yellow-700">{countdown.days}</div>
                <div className="text-xs text-yellow-600">Tage</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-700">{countdown.hours}</div>
                <div className="text-xs text-yellow-600">Std</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-700">{countdown.minutes}</div>
                <div className="text-xs text-yellow-600">Min</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-700">{countdown.seconds}</div>
                <div className="text-xs text-yellow-600">Sek</div>
              </div>
            </div>
          </div>
        ) : action.isActive && action.timeRemaining !== null ? (
          action.timeRemaining >= 1 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-sm font-semibold text-green-800">
                Gültig noch: {action.timeRemaining}d
              </p>
            </div>
          ) : countdown ? (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-orange-800 mb-2">
                Läuft ab in:
              </p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold text-orange-700">{countdown.days}</div>
                  <div className="text-xs text-orange-600">Tage</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-700">{countdown.hours}</div>
                  <div className="text-xs text-orange-600">Std</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-700">{countdown.minutes}</div>
                  <div className="text-xs text-orange-600">Min</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-700">{countdown.seconds}</div>
                  <div className="text-xs text-orange-600">Sek</div>
                </div>
              </div>
            </div>
          ) : null
        ) : null}

        {/* CTA Button */}
        <Button
          className="w-full"
          disabled={typeof primaryActionDisabled === "boolean" ? primaryActionDisabled : !action.isActive}
          onClick={handleActionClick}
          variant={action.isActive ? "default" : "secondary"}
        >
          {primaryActionLoading ? (
            "Aktiviere..."
          ) : primaryActionLabel ? (
            primaryActionLabel
          ) : action.isUpcoming ? (
            <>
              <Clock className="w-4 h-4 mr-2" />
              Startet bald
            </>
          ) : action.isActive ? (
            "Jetzt teilnehmen"
          ) : (
            "Aktion beendet"
          )}
        </Button>
      </div>
    </div>
  );
};

export default ActionCard;
